import React, { useContext } from "react";
import { CalculatorStepsContext } from "../../Store/CalculatorStepsContext";
import { CalculatorSteps } from "../../../model/CalculatorSteps";
import { rightChevron } from "../../CalculatorTool/Icons";
import { SelectBuffer } from "./SelectBuffer";
import { ValidationMessage } from "../ValidationMessage";
import {
  VALIDATION_MUST_CHOOSE_BUFFERS,
  VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_1,
  VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_2,
  VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
  VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
  getFirstValidationError,
} from "../../../model/CalculatorInputsValidation";
import { pipe } from "fp-ts/lib/pipeable";
import { fold, none, some } from "fp-ts/lib/Option";
import { InvestmentBuffer, allBuffers } from "../../../model/CalculatorInputs";
import { TooltipComponent } from "../../../utils/TooltipComponent/TooltipComponent";
import { wcmUrls } from "../../../shared/constants";
import {
  ANALYTICS_SCREENS,
  analyticsScreenChangeEvent,
} from "../../../utils/Analytics";

const bufferValidationMessages: Map<string, string> = new Map<string, string>([
  [
    VALIDATION_MUST_CHOOSE_BUFFERS,
    "Must select a buffer value for all strategies",
  ],
]);

export const Step3ChooseBuffers = () => {
  const { steps, setSteps } = useContext(CalculatorStepsContext);
  const inputStepClasses = ["input-step", "input-step-3"];
  if (steps.stepIndex === CalculatorSteps.STEP_3_CHOOSE_BUFFERS) {
    inputStepClasses.push("current-step");
  }
  const nextIcon = rightChevron("1x", "white");

  const bufferLabelId = "rila-calculator-tool-buffer-label";

  const handleNext = () => {
    const newIndex = steps.stepIndex + 1;
    const stepsWithVal = steps.validatedAtCurrentStep(steps.inputs);
    if (stepsWithVal.validation.isSuccess()) {
      setSteps(stepsWithVal.withStepIndex(newIndex).withRunValidationsOff());
      if (newIndex < ANALYTICS_SCREENS.length) {
        analyticsScreenChangeEvent(ANALYTICS_SCREENS[newIndex]);
      }
    } else {
      const firstError = getFirstValidationError(stepsWithVal.validation);
      pipe(
        firstError,
        fold(
          () => {
            setSteps(stepsWithVal.withRunValidationsOn());
          },
          (validationError) => {
            const stepsWithFirstError = stepsWithVal
              .withFirstValidationError(validationError)
              .withRunValidationsOn();
            setSteps(stepsWithFirstError);
          }
        )
      );
    }
  };

  function getAvailableBuffers(index: number) {
    const avail = steps.config.availableInvestmentStrategies;

    const strats = steps.inputs.strategyList;
    const filteredStrategies =
      index < strats.getStrategyCount()
        ? pipe(
            steps.inputs.term,
            fold(
              () => none,
              (termLength) => {
                const allocatedStrategy = strats.getAllocatedStrategy(index);
                return pipe(
                  allocatedStrategy.strategy.strategyType,
                  fold(
                    () => none,
                    (strategyType) => {
                      const strategies = avail.strategiesForTermAndType(
                        termLength,
                        strategyType
                      );
                      return some(strategies);
                    }
                  )
                );
              }
            )
          )
        : none;

    const buffersSet = new Set<InvestmentBuffer>();
    pipe(
      filteredStrategies,
      fold(
        () => {
          return;
        },
        (stratList) => {
          for (const strat of stratList) {
            buffersSet.add(strat.buffer);
          }
        }
      )
    );
    const buffers = Array.from(buffersSet);
    return buffers;
  }

  const buffersByCode = new Map<string, InvestmentBuffer>();
  for (const buf of allBuffers) {
    buffersByCode.set(buf.percentage.toString(), buf);
  }

  function setBuffer(index: number, bufferCode: string) {
    const strategyList = steps.inputs.strategyList;
    const allocatedStrategy = strategyList.getAllocatedStrategy(index);

    const buffer = buffersByCode.get(bufferCode);
    const newStrategy = buffer
      ? allocatedStrategy.strategy.withBuffer(buffer)
      : allocatedStrategy.strategy.withoutBuffer();
    const newInputs = steps.inputs.withStrategyList(
      strategyList.withAllocatedStrategyAt(
        index,
        allocatedStrategy.withStrategy(newStrategy)
      )
    );
    if (steps.shouldRunValidations) {
      setSteps(steps.validatedAtCurrentStep(newInputs));
    } else {
      setSteps(steps.withInputs(newInputs));
    }
  }

  const bufferValidationMsgId = "rila-calculator-buffer-validation-message";

  const specificIssues = [
    VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_1,
    VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_2,
    VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_3,
  ];

  const duplicateIssues = [
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
  ];

  const strategyCount = steps.inputs.strategyList.getStrategyCount();
  const strategyIndices = Array.from(Array(strategyCount).keys());

  return (
    <div id="input-section-3" className={inputStepClasses.join(" ")}>
      <div className="input-fields">
        <div className="step-header buffers">
          <div className="label-header">
            <h3 id={bufferLabelId}>
              Protection (buffer){" "}
              <TooltipComponent
                title="Protection (buffer)"
                url={wcmUrls.protectionTooltip}
                screenreaderText="More info on Protection (buffer)"
              />
            </h3>
          </div>
          <div className="description">
            <p className="instruction">Choose a protection amount.</p>
            <p className="note">
              Buffers vary by Index&nbsp;Crediting&nbsp;Strategy.
            </p>
          </div>
        </div>
        <div className="inputs step-3-inputs">
          {strategyIndices.map((i) => (
            <SelectBuffer
              key={i}
              index={i}
              validationIssue={specificIssues[i]}
              buffer={
                steps.inputs.strategyList.getAllocatedStrategy(i).strategy
                  .buffer
              }
              availableBuffers={getAvailableBuffers(i)}
              invalid={steps.validation.hasIssue(specificIssues[i])}
              isDuplicateStrategy={steps.validation.hasIssueIn(
                duplicateIssues[i]
              )}
              selectBuffer={(code: string) => setBuffer(i, code)}
              validationMessageId={bufferValidationMsgId}
            />
          ))}
        </div>
        <ValidationMessage
          id={bufferValidationMsgId}
          messagesByIssue={bufferValidationMessages}
        />
      </div>
      <div className="next-button-row">
        <button type="button" className="btn btn-next" onClick={handleNext}>
          Next {nextIcon}
        </button>
      </div>
    </div>
  );
};
